// SSE event-bus client for cloud-api's `/api/sessions/events` (Cloud's
// IACP ef331bd4 spec). Uses Fetch-Streaming + WHATWG Streams instead of
// native EventSource so we can send `Authorization: Bearer` headers
// without resorting to query-param tokens (would leak into nginx logs).
//
// Protocol:
//   - Server emits SSE frames: `event: <type>\n`, `data: <json>\n`,
//     `id: <monotonic counter>\n\n`. Blank line terminates a frame.
//   - First event after connect is `event: snapshot` with the full
//     sessions+pools dictionary (atomic initial state). Subsequent
//     events are deltas keyed by `type` (session.state, session.activity,
//     session.created, session.killed, session.pane_dirty, pool.*).
//   - `event: ping` every 15s keeps the connection alive through
//     intermediate proxies. Client ignores these for routing.
//   - On reconnect, client sends `?last_event_id=<N>` so the server's
//     ring-buffer (size 1000) can replay events since the last delivery.
//
// Use via `useEventBus()` (see hooks/useEventBus.ts) which mounts a
// singleton bus at app-startup and exposes subscribe/unsubscribe.

import type { SessionInfo, PoolInfo, AgentState } from '../api/types';
import { diag } from './diag';

// Discriminated union of all event payloads the backend can emit.
// Locked to Cloud's IACP cc91e50e schema (Phase 1 live on mac+arkserver+
// pdrei+oneal). Every frame carries `id` (monotonic counter for replay)
// and `ts` (float epoch). Pool size aggregates aren't a separate event
// — pool member-count changes through session.created/killed.
export type BusEvent =
  | {
      type: 'snapshot';
      id: number; ts: number; node: string;
      sessions: SessionInfo[];
      agent_states: Record<string, AgentState>;
      pools: PoolInfo[];
      node_status?: Record<string, unknown>;
    }
  | {
      // Runtime state from the agent-states-loop (local). For remote
      // sessions this event also carries activity_class — Cloud's
      // fed-disco-diff packs both fields into one frame.
      type: 'session.state';
      id?: number; ts?: number; name: string; node?: string;
      state: string; detail?: string;
      activity_class?: 'active' | 'warm' | 'stale' | 'cold';
      // Live now/next status — present only while thinking/tool, absent
      // once ready/idle (Cloud IACP 308c9a3b).
      live_status?: import('../api/types').LiveStatus;
      // Resume-at-tag pin state — flips when an agent is rewound to a tag /
      // un-rewound (Cloud 2026-06-23). '' / null clears the pin.
      pinned_resume_tag?: string | null;
      pinned_resume_session_id?: string | null;
    }
  | {
      // Lifecycle from session_manager (local-only). Remote lifecycle
      // changes come via fed-disco as session.state with activity_class.
      type: 'session.status';
      id?: number; ts?: number; name: string; node?: string;
      status: string;
    }
  | {
      type: 'session.created';
      id?: number; ts?: number; name: string; node?: string;
      session: SessionInfo;
    }
  | {
      type: 'session.killed';
      id?: number; ts?: number; name: string; node?: string;
    }
  | {
      type: 'session.pane_dirty';
      id?: number; ts?: number; name: string; node?: string; hash?: string;
    }
  | {
      type: 'pool.member.acquired';
      id?: number; ts?: number; node?: string;
      pool: string; name: string; acquired_by?: string;
    }
  | {
      type: 'pool.member.released';
      id?: number; ts?: number; node?: string;
      pool: string; name: string;
    }
  | {
      // issue-api dispatched an issue/goal to an agent. cloud-api mirrors it
      // federation-wide via _emit_federated, so every FE sees it regardless of node
      // (#1257 / Alex's "aha jetzt geht's da weiter"). Fields are flat (bus convention).
      type: 'issue.dispatched';
      id?: number; ts?: number | string; node?: string;
      issue_id: number;
      assignee: string;
      title: string;
      goal_id?: number | null;
      dispatch_status?: 'active' | 'queued';
      // Owner identity (Alex 2026-07-23: "issue infos die zb jascha anlegt soll
      // nur er sehen"). The issue RECORD carries reporter (user-UUID) +
      // reporter_type + tenant_id — the dispatch EVENT does not yet, so these
      // are optional until issue-api adds them. As soon as `reporter` arrives,
      // StatusToasts suppresses foreign users' dispatches.
      reporter?: string | null;
      reporter_type?: string | null;
      tenant_id?: string | null;
    }
  | {
      // cloud-api capped auto-login at 3 attempts; after that it STOPs + escalates via
      // this federation-wide event (Cloud 2026-06-30). Operator alert → manual /login.
      type: 'agent.login_failed';
      id?: number; ts?: number | string; node?: string;
      name: string;
      attempts?: number;
    }
  | { type: 'ping' };

export type BusEventType = BusEvent['type'];

export type BusStatus = 'idle' | 'connecting' | 'live' | 'reconnecting' | 'failed';

type Handler<T extends BusEventType> = (ev: Extract<BusEvent, { type: T }>) => void;
type AnyHandler = (ev: BusEvent) => void;

export interface SseBus {
  connect(): void;
  disconnect(): void;
  subscribe<T extends BusEventType>(type: T, handler: Handler<T>): () => void;
  subscribeAll(handler: AnyHandler): () => void;
  onStatus(handler: (s: BusStatus) => void): () => void;
  status(): BusStatus;
}

const AUTH_STORAGE_KEY = 'ark-auth';
function bearerToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    return stored?.access_token || null;
  } catch { return null; }
}

interface SseBusOptions {
  url?: string;
  // Reconnect cadence — exponential up to maxBackoffMs. Reset on a
  // successfully received event (not just connection-open) so a server
  // that opens-then-immediately-closes still triggers backoff growth.
  initialBackoffMs?: number;
  maxBackoffMs?: number;
}

export function createSseBus(opts: SseBusOptions = {}): SseBus {
  const url = opts.url ?? '/api/sessions/events';
  const initialBackoff = opts.initialBackoffMs ?? 1000;
  const maxBackoff = opts.maxBackoffMs ?? 30_000;

  const handlersByType = new Map<BusEventType, Set<AnyHandler>>();
  const allHandlers = new Set<AnyHandler>();
  const statusHandlers = new Set<(s: BusStatus) => void>();
  let currentStatus: BusStatus = 'idle';
  let abortCtl: AbortController | null = null;
  let backoffMs = initialBackoff;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let lastEventId: string | null = null;

  let liveSinceTs = 0;
  const setStatus = (s: BusStatus) => {
    if (currentStatus === s) return;
    const prev = currentStatus;
    currentStatus = s;
    // Diagnostics: log every transition with how long we held the prior
    // state. The key signal is "live → reconnecting" with the live
    // duration — that's the SSE connection lifetime on the user's actual
    // network path (the thing that was dying every ~60-120s pre-fix).
    if (s === 'live') liveSinceTs = Date.now();
    if (prev === 'live' && s !== 'live') {
      const heldSec = liveSinceTs ? Math.round((Date.now() - liveSinceTs) / 1000) : null;
      diag(`SSE ${prev} → ${s}`, { liveDurationSec: heldSec });
    } else {
      diag(`SSE ${prev} → ${s}`);
    }
    for (const h of statusHandlers) {
      try { h(s); } catch (e) { console.error('[sseBus] status handler threw:', e); }
    }
  };

  const dispatch = (ev: BusEvent) => {
    // Log snapshots with their agent count — a snapshot carrying far
    // fewer agents than usual is the "remote peers missing" signature.
    if (ev.type === 'snapshot') {
      diag('SSE snapshot received', {
        agents: ev.sessions?.length ?? 0,
        pools: ev.pools?.length ?? 0,
      });
    }
    for (const h of allHandlers) {
      try { h(ev); } catch (e) { console.error('[sseBus] handler threw:', e); }
    }
    const set = handlersByType.get(ev.type);
    if (set) {
      for (const h of set) {
        try { h(ev); } catch (e) { console.error('[sseBus] handler threw:', e); }
      }
    }
  };

  // Parse a single SSE frame block (separated by blank line) into a
  // BusEvent. Returns null for ping frames or malformed input.
  const parseFrame = (block: string): { id?: string; ev: BusEvent | null } => {
    let eventType: string | null = null;
    let dataLines: string[] = [];
    let id: string | undefined;
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
      else if (line.startsWith('id:')) id = line.slice(3).trim();
      else if (line.startsWith(':')) { /* comment, ignore */ }
    }
    if (!eventType) return { id, ev: null };
    if (eventType === 'ping') return { id, ev: { type: 'ping' } };
    if (dataLines.length === 0) return { id, ev: null };
    try {
      const payload = JSON.parse(dataLines.join('\n'));
      // Trust the backend to put the discriminator on the payload too,
      // but fall back to the `event:` header so a backend that omits it
      // still routes correctly.
      const ev = (payload && typeof payload === 'object' && 'type' in payload)
        ? payload as BusEvent
        : { type: eventType, ...payload } as BusEvent;
      return { id, ev };
    } catch (e) {
      console.warn('[sseBus] failed to parse frame data:', e, block.slice(0, 200));
      return { id, ev: null };
    }
  };

  const scheduleReconnect = () => {
    if (stopped) return;
    if (reconnectTimer) return;
    setStatus('reconnecting');
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void runConnection();
    }, backoffMs);
    backoffMs = Math.min(backoffMs * 2, maxBackoff);
  };

  // Snappy recovery on the two moments the user actually notices: coming
  // back to the tab, or regaining connectivity. With the 30s safety-poll
  // gone (Alex 2026-06-19), these wake triggers carry recovery — kick an
  // immediate reconnect instead of waiting out the exponential backoff.
  // Skip if we're already live/connecting (nothing to recover).
  const wake = () => {
    if (stopped) return;
    if (currentStatus === 'live' || currentStatus === 'connecting') return;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    backoffMs = initialBackoff;
    void runConnection();
  };
  const onVisibility = () => { if (typeof document === 'undefined' || !document.hidden) wake(); };

  const runConnection = async () => {
    if (stopped) return;
    setStatus('connecting');
    abortCtl = new AbortController();
    const token = bearerToken();
    if (!token) {
      // No auth state — wait + retry; user might log in later.
      diag('SSE connect aborted: no auth token');
      setStatus('failed');
      scheduleReconnect();
      return;
    }
    const fullUrl = lastEventId
      ? `${url}?last_event_id=${encodeURIComponent(lastEventId)}`
      : url;
    try {
      const res = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
        },
        signal: abortCtl.signal,
      });
      if (!res.ok || !res.body) {
        // 401/403 = auth bad, longer backoff; other 4xx/5xx = transient.
        // Log the actual status so connecting→failed isn't a black box —
        // distinguishes auth (401) from proxy (502/504) from no-body.
        diag('SSE connect failed', { status: res.status, hasBody: !!res.body });
        setStatus('failed');
        scheduleReconnect();
        return;
      }
      setStatus('live');
      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        // Reset backoff on first successful chunk
        if (currentStatus === 'live' && backoffMs !== initialBackoff) {
          backoffMs = initialBackoff;
        }
        buffer += decoder.decode(value, { stream: true });
        // SSE separator is a blank line (`\n\n` or `\r\n\r\n`).
        let sepIdx: number;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const { id, ev } = parseFrame(block);
          if (id) lastEventId = id;
          if (ev) dispatch(ev);
        }
      }
      // Server closed the connection cleanly — schedule a reconnect.
      diag('SSE stream ended (server closed) — reconnecting');
      scheduleReconnect();
    } catch (e) {
      // AbortError = intentional disconnect (unmount/visibility), swallow.
      if ((e as Error)?.name === 'AbortError') return;
      diag('SSE stream threw — reconnecting', { err: (e as Error)?.message?.slice(0, 80) });
      console.warn('[sseBus] connection threw:', e);
      scheduleReconnect();
    }
  };

  return {
    connect() {
      stopped = false;
      backoffMs = initialBackoff;
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.addEventListener('online', wake);
      void runConnection();
    },
    disconnect() {
      stopped = true;
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
      if (typeof window !== 'undefined') window.removeEventListener('online', wake);
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      if (abortCtl) { abortCtl.abort(); abortCtl = null; }
      setStatus('idle');
    },
    subscribe<T extends BusEventType>(type: T, handler: Handler<T>) {
      let set = handlersByType.get(type);
      if (!set) { set = new Set(); handlersByType.set(type, set); }
      set.add(handler as AnyHandler);
      return () => { set!.delete(handler as AnyHandler); };
    },
    subscribeAll(handler: AnyHandler) {
      allHandlers.add(handler);
      return () => { allHandlers.delete(handler); };
    },
    onStatus(handler) {
      statusHandlers.add(handler);
      // Replay current status synchronously so late subscribers don't
      // have to wait for the next transition to learn the state.
      try { handler(currentStatus); } catch (e) { console.error('[sseBus]', e); }
      return () => { statusHandlers.delete(handler); };
    },
    status() {
      return currentStatus;
    },
  };
}

// Singleton bus per tab. Created lazily on first access so unit tests
// can instantiate fresh buses via createSseBus() directly.
let _bus: SseBus | null = null;
export function getSseBus(): SseBus {
  if (!_bus) _bus = createSseBus();
  return _bus;
}
